import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, Users, ChevronUp, GripHorizontal } from "lucide-react";
import {
  unpinTicket,
  updateBubbleState,
  toggleMinimized,
} from "../lib/pinnedTickets";
import { formatTicketCode } from "../lib/ticketCode";

/**
 * Floating "pinned ticket" bubble — one per active ticket the visitor has
 * chosen to pin. Stays visible across every visitor route, is draggable
 * with the finger, can be minimised (collapsed to a round chip showing
 * just the code) and closed (×).
 *
 * Synchronisation : the parent <PinnedTicketsLayer/> hands us the latest
 * `ticket` payload polled via `useActiveTickets` — so position, status,
 * "X devant", "called" badge all stay live in real time.
 *
 * Future native-port note : the visual hierarchy below maps 1-to-1 onto
 * an Android Bubble (BubbleMetadata) icon + small + expanded view. The
 * pure-data props received (ticket + pin) are framework-agnostic.
 */
const BUBBLE_W_EXPANDED = 268; // px — pill width (expanded)
const BUBBLE_W_MIN = 60; // px — round chip width (minimised)
const BUBBLE_H = 56;
const MARGIN = 12;

export default function PinnedTicketBubble({ ticket, pin, stackIndex }) {
  const nav = useNavigate();
  const containerRef = useRef(null);
  // Default stacked position : right edge, vertically stacked from bottom up.
  const [pos, setPos] = useState(() =>
    pin.x != null && pin.y != null
      ? { x: pin.x, y: pin.y }
      : computeDefaultPos(stackIndex, pin.minimized),
  );
  // Recompute the default position if the viewport changes (e.g. rotation)
  // AND the user hasn't manually placed the bubble yet.
  useEffect(() => {
    if (pin.x == null || pin.y == null) {
      setPos(computeDefaultPos(stackIndex, pin.minimized));
    }
    const onResize = () => {
      setPos((p) => clampToViewport(p, pin.minimized));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stackIndex, pin.minimized]);

  const code = formatTicketCode(ticket);
  const isCalled = ticket?.status === "called";
  const position = typeof ticket?.position === "number" ? ticket.position : null;

  // ------------------------------------------------------------------
  // DRAG (pointer events — covers touch + mouse uniformly)
  // ------------------------------------------------------------------
  const dragStateRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const onPointerDown = useCallback(
    (e) => {
      // Ignore drags initiated on the close / expand buttons
      if (e.target.closest("[data-bubble-action]")) return;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      dragStateRef.current = {
        dx: e.clientX - pos.x,
        dy: e.clientY - pos.y,
        moved: false,
        startX: e.clientX,
        startY: e.clientY,
      };
      setDragging(true);
    },
    [pos.x, pos.y],
  );

  const onPointerMove = useCallback(
    (e) => {
      const s = dragStateRef.current;
      if (!s) return;
      const dx = e.clientX - s.startX;
      const dy = e.clientY - s.startY;
      if (!s.moved && Math.hypot(dx, dy) > 4) s.moved = true;
      const next = clampToViewport(
        { x: e.clientX - s.dx, y: e.clientY - s.dy },
        pin.minimized,
      );
      setPos(next);
    },
    [pin.minimized],
  );

  const onPointerUp = useCallback(
    (e) => {
      const s = dragStateRef.current;
      if (!s) return;
      dragStateRef.current = null;
      setDragging(false);
      // Persist final position so the visitor finds the bubble where they
      // left it next time they navigate.
      if (s.moved) {
        updateBubbleState(ticket.id, { x: pos.x, y: pos.y });
      } else {
        // Tap (no drag) on the body → open the ticket detail page.
        nav(`/my-ticket?focus=${encodeURIComponent(ticket.id)}`);
      }
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    },
    [nav, pos.x, pos.y, ticket?.id],
  );

  // ------------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------------
  const minimized = !!pin.minimized;
  const width = minimized ? BUBBLE_W_MIN : BUBBLE_W_EXPANDED;

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-label={`Ticket épinglé ${code}`}
      data-testid={`pinned-bubble-${ticket.id}`}
      style={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width,
        height: BUBBLE_H,
        zIndex: 70,
        touchAction: "none",
        transition: dragging
          ? "none"
          : "width 220ms ease-out, transform 200ms ease-out",
        transform: dragging ? "scale(1.03)" : "scale(1)",
        userSelect: "none",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div
        className={`relative w-full h-full rounded-full bg-white flex items-center ${
          minimized ? "justify-center" : "justify-between gap-2 pl-3 pr-12"
        } shadow-[0_10px_30px_rgba(15,23,42,0.18)] border border-border ${
          isCalled ? "ring-2 ring-[#34C759]" : ""
        }`}
      >
        {/* Drag handle (small dots) at the top — pure visual affordance, the
            whole bubble body is the drag target. */}
        <span className="absolute -top-2 left-1/2 -translate-x-1/2 inline-flex items-center justify-center w-8 h-4 bg-white rounded-full border border-border text-subtle pointer-events-none">
          <GripHorizontal size={12} />
        </span>

        {minimized ? (
          // Round chip — code only.
          <span
            className="font-mono font-black text-brand text-sm leading-none"
            data-testid={`pinned-bubble-code-${ticket.id}`}
          >
            {code}
          </span>
        ) : (
          <>
            {/* LEFT : tilted ticket icon (custom SVG) */}
            <span
              className="inline-flex items-center justify-center shrink-0 w-9 h-9 text-brand"
              style={{ transform: "rotate(-10deg)" }}
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 72 36"
                className="w-full h-full"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinejoin="round"
                strokeLinecap="round"
              >
                <path
                  d="M 6 4 L 66 4 A 4 4 0 0 1 70 8 L 70 14 A 4 4 0 0 0 70 22 L 70 28 A 4 4 0 0 1 66 32 L 6 32 A 4 4 0 0 1 2 28 L 2 22 A 4 4 0 0 0 2 14 L 2 8 A 4 4 0 0 1 6 4 Z"
                />
                <line
                  x1="36"
                  y1="6"
                  x2="36"
                  y2="30"
                  strokeDasharray="2 2.4"
                  strokeWidth="1.4"
                />
              </svg>
            </span>
            {/* MIDDLE : code + position */}
            <div className="flex-1 min-w-0 flex items-center gap-2.5">
              <span
                className="font-mono font-black text-brand text-lg leading-none"
                data-testid={`pinned-bubble-code-${ticket.id}`}
              >
                {code}
              </span>
              <span className="w-px h-6 bg-border shrink-0" />
              <div className="flex items-center gap-1.5 min-w-0">
                {isCalled ? (
                  <span
                    className="text-[#0A7F2E] font-bold text-xs uppercase tracking-wider truncate"
                    data-testid={`pinned-bubble-status-${ticket.id}`}
                  >
                    C'est à vous !
                  </span>
                ) : (
                  <>
                    <Users size={14} className="text-subtle shrink-0" />
                    <span
                      className="text-ink text-sm leading-tight"
                      data-testid={`pinned-bubble-status-${ticket.id}`}
                    >
                      {position === 0 ? (
                        <span className="font-bold">Prochain</span>
                      ) : position == null ? (
                        "—"
                      ) : (
                        <>
                          <span className="font-bold">{position}</span>{" "}
                          <span className="text-subtle text-xs">
                            avant vous
                          </span>
                        </>
                      )}
                    </span>
                  </>
                )}
              </div>
            </div>
          </>
        )}

        {/* RIGHT : minimise + close (overlapping the round edge) */}
        {!minimized && (
          <button
            type="button"
            data-bubble-action
            onClick={(e) => {
              e.stopPropagation();
              toggleMinimized(ticket.id);
            }}
            className="absolute right-12 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-surface text-subtle hover:text-ink flex items-center justify-center"
            data-testid={`pinned-bubble-minimize-${ticket.id}`}
            aria-label="Réduire la bulle"
          >
            <ChevronUp size={14} />
          </button>
        )}
        <button
          type="button"
          data-bubble-action
          onClick={(e) => {
            e.stopPropagation();
            if (minimized) {
              toggleMinimized(ticket.id);
            } else {
              unpinTicket(ticket.id);
            }
          }}
          className={`${
            minimized
              ? "absolute -right-1 -top-1 w-5 h-5"
              : "absolute -right-2 top-1/2 -translate-y-1/2 w-9 h-9"
          } rounded-full bg-white border border-border text-ink hover:text-destructive flex items-center justify-center shadow-md`}
          data-testid={
            minimized
              ? `pinned-bubble-expand-${ticket.id}`
              : `pinned-bubble-close-${ticket.id}`
          }
          aria-label={minimized ? "Agrandir la bulle" : "Fermer la bulle"}
        >
          <X size={minimized ? 10 : 16} />
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Helpers (pure functions, framework-agnostic — easy to port to native)
// ----------------------------------------------------------------------

function computeDefaultPos(stackIndex, minimized) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 360;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const w = minimized ? BUBBLE_W_MIN : BUBBLE_W_EXPANDED;
  const x = vw - w - MARGIN;
  // Stack from bottom up : index 0 = lowest. Bottom ~ 110px to avoid the
  // mobile browser chrome and tab bar.
  const y = vh - 110 - stackIndex * (BUBBLE_H + 12);
  return clampToViewport({ x, y }, minimized);
}

function clampToViewport(p, minimized) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 360;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const w = minimized ? BUBBLE_W_MIN : BUBBLE_W_EXPANDED;
  const x = Math.min(Math.max(MARGIN, p.x), vw - w - MARGIN);
  const y = Math.min(Math.max(MARGIN, p.y), vh - BUBBLE_H - MARGIN);
  return { x, y };
}
